import {parseStyleToText} from "../../../../common/preview/parse-style-to-text";
import {CUSTOM_RECT_TYPE} from "../../../constants/custom-fabric-types";
import {KEY_ADD_HTML, KEY_HTML} from '@frontend/constants/templates-field-prefixes';
import {getAllObjects} from "@frontend/services/helpers/group-banner-helpers";

const aliasType = {
    'textbox': 'text',
    [CUSTOM_RECT_TYPE]: 'rectangle',
    'rect': 'rectangle',
    'circle': 'ellipse',
    'ellipse': 'ellipse',
    'image': 'image',
    'line': 'rectangle',
}

const lineModifier = (data) => ({
   ...data,
    strokeWidth: 0,
})

const MODIFIERS = {
    'line': lineModifier
}

// Fabric's matrix math returns values like 1019.9999999999999, which the backend truncates to the previous pixel
const snapFloat = value => Math.round(value * 1e6) / 1e6;

const serializerBuilder = options => object => {
    const { maskSettings } = options;
    const position = object.getPointByOrigin('left', 'top');
    const mask = maskSettings.find(mask => mask.id === object.id.replace(/_/g, '')) || {};
    let fill = object.fill;

    if (object.fill instanceof fabric.Gradient) {
        fill = object.fill.colorStops.map(({ color }) => color);
    }
    const data = {
        ...object.toJSON(),
        ...(object.type === 'textbox' ? {
            text: parseStyleToText(object.text.split('\n'), object.styles),
            lineHeight: object.textLines.length > 1 ? Math.max(...object.__lineHeights) : 0,
            is_generated_bullet: !!object.isGeneratedBullet,
            split_bullets_child: object.splitBulletsChild ?? null,
            styles: (() => {
                if (!object.styles) return [];
                const result = [];

                Object.keys(object.styles).forEach(lineIdx => {
                    const lineStyles = object.styles[lineIdx] || {};
                    const lineResult = [];

                    Object.keys(lineStyles).forEach(charIdx => {
                        lineResult[parseInt(charIdx)] = lineStyles[charIdx];
                    });
                    result[parseInt(lineIdx)] = lineResult;
                });
                return result;
            })()
        } : {}),
        ...(object.type === CUSTOM_RECT_TYPE ? {
            rounding: object.rx || 0,
        } : {}),
        id: object.id,
        element_id: object.element_id,
        type: aliasType[object.type] || object.type,
        width: snapFloat(object.getScaledWidth()),
        height: snapFloat(object.getScaledHeight()),
        left: snapFloat(position.x),
        top: snapFloat(position.y),
        color: object.fontColor,
        cornerSize: object.rx,
        fontName: window.fontFamilyManager.getFontByIndex(object.fontFamily)?.name,
        corners: object.corners,
        order: object.order,
        fill,
        mask,
        verticalOffset: object.verticalOffset ?? null,
        link: object?.templateField?.getLink?.() || object.link || '',
        ...(object.type === 'image' ? {
            mirrorShadow: object.mirrorShadow ? structuredClone(object.mirrorShadow) : null,
            mirrorShadowGroupId: object.mirrorShadowGroupId ?? null,
        } : {}),
        ...(object.id && (object.id.startsWith(KEY_HTML) || object.id.startsWith(KEY_ADD_HTML)) ? { src: object.toDataURL() } : {}),
    };
    
    if (data.type === 'text' && object.tocPageNumber) {
      data.type = 'tocLine';
      data.title = object.text;
      data.page = object.tocPageNumber;
    }

    if (!MODIFIERS[object.type]) {
        return data;
    }

    return MODIFIERS[object.type](data)
}

export const linesFilter = object => Object.keys(aliasType).some(l => l === object.type) && object.element_id

// extra serializers override props created from the simple one
export const serializerFactory = (options, extraSerializers = []) => objects => {
    const serializer = serializerBuilder(options)

    let {objects: localObjects} = getAllObjects(objects);
    
    // Handle also groups: ungroup them and add group elements to localObjects array
    for (const object of localObjects) {
      if (object.group !== undefined) {
        object.left = object.group.left + object.left + object.group.width / 2;
        object.top = object.group.top + object.top + object.group.height / 2;
      }
    }
    
    localObjects = localObjects.filter(linesFilter);
    
    return localObjects.map(object => {
            const results = extraSerializers.map(extraSerializer => extraSerializer(object));

            results.unshift(serializer(object))

            return results.reduce((acc, current) => Object.assign(acc, current), {})
        })
}
